import ListWrapper from "../utils/ListWrapper";
import Provider from "./Provider";
import DateTime from "../core/DateTime";
import PortfolioProject from "./PortfolioProject";
import DFile from "../classes/DFile";
import BusinessAddress from "./BusinessAddress";
import BusinessStatus from "../classes/BusinessStatus";
import DBObject from "../utils/DBObject";
import CloneContext from "../utils/CloneContext";
import CollectionUtils from "../utils/CollectionUtils";

export default class Business extends DBObject {
  private static readonly _ADDRESS: number = 0;
  private static readonly _BUSINESSSTATUS: number = 1;
  private static readonly _CREATEDDATE: number = 2;
  private static readonly _DETAILS: number = 3;
  private static readonly _LOGO: number = 4;
  private static readonly _NAME: number = 5;
  private static readonly _PORTFOLIOPROJECTS: number = 6;
  private static readonly _PROVIDERS: number = 7;
  public id: number = 0;
  public otherMaster: DBObject;
  private _name: string = "";
  private _logo: DFile = null;
  private _details: string = "";
  private _providers: Array<Provider> = ListWrapper.inverse(
    this,
    "providers",
    Business._PROVIDERS
  );
  private _address: BusinessAddress = null;
  private _portfolioProjects: Array<PortfolioProject> = ListWrapper.inverse(
    this,
    "portfolioProjects",
    Business._PORTFOLIOPROJECTS
  );
  private _businessStatus: BusinessStatus = BusinessStatus.Pending;
  private _createdDate: DateTime = null;
  public constructor(
    d3eParams?: Partial<{
      address: BusinessAddress;
      businessStatus: BusinessStatus;
      createdDate: DateTime;
      details: string;
      logo: DFile;
      name: string;
      portfolioProjects: Array<PortfolioProject>;
      providers: Array<Provider>;
    }>
  ) {
    super();

    this.setAddress(d3eParams?.address ?? null);

    this.setBusinessStatus(d3eParams?.businessStatus ?? BusinessStatus.Pending);

    this.setCreatedDate(d3eParams?.createdDate ?? null);

    this.setDetails(d3eParams?.details ?? "");

    this.setLogo(d3eParams?.logo ?? null);

    this.setName(d3eParams?.name ?? "");

    this.setPortfolioProjects(
      d3eParams?.portfolioProjects ??
        ListWrapper.inverse(
          this,
          "portfolioProjects",
          Business._PORTFOLIOPROJECTS
        )
    );

    this.setProviders(
      d3eParams?.providers ??
        ListWrapper.inverse(this, "providers", Business._PROVIDERS)
    );
  }
  public get d3eType(): string {
    return "Business";
  }
  public static updateProvidersInverse(on: any, obj: any, type: string): void {
    if ("Insert" === type) {
      on.addToProviders(obj as Provider);
    }

    if ("Delete" === type) {
      on.removeFromProviders(obj as Provider);
    }
  }
  public static updateAddressInverse(on: any, obj: any, type: string): void {
    if ("Insert" === type) {
      on.addToAddress(obj as BusinessAddress);
    }

    if ("Delete" === type) {
      on.removeFromAddress(obj as BusinessAddress);
    }
  }
  public static updatePortfolioProjectsInverse(
    on: any,
    obj: any,
    type: string
  ): void {
    if ("Insert" === type) {
      on.addToPortfolioProjects(obj as PortfolioProject);
    }

    if ("Delete" === type) {
      on.removeFromPortfolioProjects(obj as PortfolioProject);
    }
  }
  public clear(): void {
    this.d3eChanges.clear();
  }
  public get name(): string {
    return this._name;
  }
  public setName(val: string): void {
    let isValChanged: boolean = this._name !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(Business._NAME, this._name);

    this._name = val;

    this.fire("name", this);
  }
  public get logo(): DFile {
    return this._logo;
  }
  public setLogo(val: DFile): void {
    let isValChanged: boolean = this._logo !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(Business._LOGO, this._logo);

    this._logo = val;

    this.fire("logo", this);
  }
  public get details(): string {
    return this._details;
  }
  public setDetails(val: string): void {
    let isValChanged: boolean = this._details !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(Business._DETAILS, this._details);

    this._details = val;

    this.fire("details", this);
  }
  public get providers(): Array<Provider> {
    return this._providers;
  }
  public setProviders(val: Array<Provider>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this._providers,
      val
    );

    if (!isValChanged) {
      return;
    }

    if (!this.d3eChanges.contains(Business._PROVIDERS)) {
      let _old: Array<Provider> = Array.from(this._providers);

      this.updateD3EChanges(Business._PROVIDERS, _old);
    }

    this.updateObservableColl("providers", this._providers, val);

    this._providers.clear();

    this._providers.addAll(val);

    this.fire("providers", this);
  }
  public addToProviders(val: Provider, index: number = -1): void {
    let _old: Array<Provider> = [];

    let _isNewChange: boolean = !this.d3eChanges.contains(Business._PROVIDERS);

    if (_isNewChange) {
      _old = Array.from(this._providers);
    }

    if (index === -1) {
      if (!this._providers.contains(val)) this._providers.add(val);
    } else {
      this._providers.remove(this._providers.elementAt(index));

      this._providers.add(val);
    }

    this.fire("providers", this, val, true);

    this.updateObservable("providers", null, val);

    if (_isNewChange) {
      this.updateD3EChanges(Business._PROVIDERS, _old);
    }
  }
  public removeFromProviders(val: Provider): void {
    let _old: Array<Provider> = [];

    let _isNewChange: boolean = !this.d3eChanges.contains(Business._PROVIDERS);

    if (_isNewChange) {
      _old = Array.from(this._providers);
    }

    this._providers.remove(val);

    this.fire("providers", this, val, false);

    if (_isNewChange) {
      this.updateD3EChanges(Business._PROVIDERS, _old);
    }
  }
  public get address(): BusinessAddress {
    return this._address;
  }
  public setAddress(val: BusinessAddress): void {
    let isValChanged: boolean = this._address !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(Business._ADDRESS, this._address);

    this.updateObservable("address", this._address, val);

    this._address = val;

    this.fire("address", this);
  }
  public get portfolioProjects(): Array<PortfolioProject> {
    return this._portfolioProjects;
  }
  public setPortfolioProjects(val: Array<PortfolioProject>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this._portfolioProjects,
      val
    );

    if (!isValChanged) {
      return;
    }

    if (!this.d3eChanges.contains(Business._PORTFOLIOPROJECTS)) {
      let _old: Array<PortfolioProject> = Array.from(this._portfolioProjects);

      this.updateD3EChanges(Business._PORTFOLIOPROJECTS, _old);
    }

    this.updateObservableColl(
      "portfolioProjects",
      this._portfolioProjects,
      val
    );

    this._portfolioProjects.clear();

    this._portfolioProjects.addAll(val);

    this.fire("portfolioProjects", this);
  }
  public addToPortfolioProjects(
    val: PortfolioProject,
    index: number = -1
  ): void {
    let _old: Array<PortfolioProject> = [];

    let _isNewChange: boolean = !this.d3eChanges.contains(
      Business._PORTFOLIOPROJECTS
    );

    if (_isNewChange) {
      _old = Array.from(this._portfolioProjects);
    }

    if (index === -1) {
      if (!this._portfolioProjects.contains(val))
        this._portfolioProjects.add(val);
    } else {
      this._portfolioProjects.remove(this._portfolioProjects.elementAt(index));

      this._portfolioProjects.add(val);
    }

    this.fire("portfolioProjects", this, val, true);

    this.updateObservable("portfolioProjects", null, val);

    if (_isNewChange) {
      this.updateD3EChanges(Business._PORTFOLIOPROJECTS, _old);
    }
  }
  public removeFromPortfolioProjects(val: PortfolioProject): void {
    let _old: Array<PortfolioProject> = [];

    let _isNewChange: boolean = !this.d3eChanges.contains(
      Business._PORTFOLIOPROJECTS
    );

    if (_isNewChange) {
      _old = Array.from(this._portfolioProjects);
    }

    this._portfolioProjects.remove(val);

    this.fire("portfolioProjects", this, val, false);

    if (_isNewChange) {
      this.updateD3EChanges(Business._PORTFOLIOPROJECTS, _old);
    }
  }
  public get businessStatus(): BusinessStatus {
    return this._businessStatus;
  }
  public setBusinessStatus(val: BusinessStatus): void {
    let isValChanged: boolean = this._businessStatus !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(Business._BUSINESSSTATUS, this._businessStatus.index);

    this._businessStatus = val;

    this.fire("businessStatus", this);
  }
  public get createdDate(): DateTime {
    return this._createdDate;
  }
  public setCreatedDate(val: DateTime): void {
    let isValChanged: boolean = this._createdDate !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(Business._CREATEDDATE, this._createdDate);

    this._createdDate = val;

    this.fire("createdDate", this);
  }
  public get(field: number): any {
    switch (field) {
      case Business._NAME: {
        return this._name;
      }

      case Business._LOGO: {
        return this._logo;
      }

      case Business._DETAILS: {
        return this._details;
      }

      case Business._PROVIDERS: {
        return this._providers;
      }

      case Business._ADDRESS: {
        return this._address;
      }

      case Business._PORTFOLIOPROJECTS: {
        return this._portfolioProjects;
      }

      case Business._BUSINESSSTATUS: {
        return this._businessStatus.index;
      }

      case Business._CREATEDDATE: {
        return this._createdDate;
      }
      default: {
        return null;
      }
    }
  }
  public updateD3EChanges(index: number, value: any): void {
    if (this.lockedChanges()) {
      return;
    }

    super.updateD3EChanges(index, value);
  }
  public restore(): void {
    /*
TODO: Might be removed
*/

    this.d3eChanges.restore(this);
  }
  public deepClone(clearId = true): Business {
    let ctx: CloneContext = new CloneContext({ "clearId": clearId });

    return ctx.startClone(this);
  }
  public collectChildValues(ctx: CloneContext): void {}
  public deepCloneIntoObj(dbObj: DBObject, ctx: CloneContext): void {
    let obj: Business = dbObj as Business;

    obj.id = this.id;

    obj.setName(this._name);

    obj.setLogo(this._logo);

    obj.setDetails(this._details);

    obj.setProviders(this._providers);

    obj.setAddress(this._address);

    obj.setPortfolioProjects(this._portfolioProjects);

    obj.setBusinessStatus(this._businessStatus);

    obj.setCreatedDate(this._createdDate);
  }
  public set(field: number, value: any): void {
    switch (field) {
      case Business._NAME: {
        this.setName(value as string);
        break;
      }

      case Business._LOGO: {
        this.setLogo(value as DFile);
        break;
      }

      case Business._DETAILS: {
        this.setDetails(value as string);
        break;
      }

      case Business._PROVIDERS: {
        this.setProviders((value as Array<any>).cast<Provider>().toList());
        break;
      }

      case Business._ADDRESS: {
        this.setAddress(value as BusinessAddress);
        break;
      }

      case Business._PORTFOLIOPROJECTS: {
        this.setPortfolioProjects(
          (value as Array<any>).cast<PortfolioProject>().toList()
        );
        break;
      }

      case Business._BUSINESSSTATUS: {
        this.setBusinessStatus(BusinessStatus.values[value as number]);
        break;
      }

      case Business._CREATEDDATE: {
        this.setCreatedDate(value as DateTime);
        break;
      }
    }
  }
}
